import { z } from 'zod';

/** CSS color in #rgb, #rrggbb or #rrggbbaa form. */
export const ColorSchema = z.string().regex(/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/);
export type Color = z.infer<typeof ColorSchema>;

export const DashSchema = z.enum(['solid', 'dashed', 'dotted', 'fine']);
export type Dash = z.infer<typeof DashSchema>;

export const StrokeSchema = z.object({
  color: ColorSchema,
  width: z.number().min(0).max(1000),
  dash: DashSchema,
});
export type Stroke = z.infer<typeof StrokeSchema>;

export const FillSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('none') }),
  z.object({ kind: z.literal('solid'), color: ColorSchema }),
  z.object({
    kind: z.literal('lines'),
    color: ColorSchema,
    background: ColorSchema.optional(),
    spacing: z.number().positive().max(10000).optional(),
    angle: z.number().optional(),
  }),
  z.object({
    kind: z.literal('grid'),
    color: ColorSchema,
    background: ColorSchema.optional(),
    spacing: z.number().positive().max(10000).optional(),
  }),
]);
export type Fill = z.infer<typeof FillSchema>;

export const TextAlignSchema = z.enum(['left', 'center', 'right']);
export const TextVAlignSchema = z.enum(['top', 'middle', 'bottom']);

export const TextStyleSchema = z.object({
  fontSize: z.number().positive().max(10000),
  fontFamily: z.string().max(200).optional(),
  color: ColorSchema,
  align: TextAlignSchema,
  valign: TextVAlignSchema,
  bold: z.boolean().optional(),
  italic: z.boolean().optional(),
  lineHeight: z.number().positive().max(10).optional(),
});
export type TextStyle = z.infer<typeof TextStyleSchema>;

export const StyleSchema = z
  .object({
    stroke: StrokeSchema.nullable(),
    fill: FillSchema,
    opacity: z.number().min(0).max(1),
    cornerRadius: z.number().min(0).optional(),
    text: TextStyleSchema.optional(),
  })
  .passthrough();
export type Style = z.infer<typeof StyleSchema>;

/** Bundled with the app (and embedded in SVG export), with the system stack as fallback. */
export const DEFAULT_FONT_FAMILY = '"Inter", system-ui, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';
